// Módulo compartilhado da configuração de trocas (handover) por tipo de time.
// Única fonte de verdade para opções, tipos, defaults, validação e montagem
// de payload — consumido pela edição (ExchangeConfigTab) e criação (ExchangeConfigForm/store).

export type HandoverTiming = 'BEFORE' | 'AFTER'
export type HandoverReference = 'STATION_START' | 'STATION_END'

export type HandoverRestrictionMode = 'disabled' | 'admin_only' | 'enabled'

export interface HandoverSelectOption<T extends string = string> {
  title: string
  value: T
}

// Config de trocas + jornada de UM time (interno ou externo).
// A jornada (limitConsecutiveHours/maxConsecutiveHours/minRestHours/
// consecutiveHoursOurTeamOnly) é INDEPENDENTE de allowDoctorStationHandover:
// a regra vale para alocação, não só para troca.
export interface TeamHandoverConfig {
  allowDoctorStationHandover: boolean
  requiresAdminApproval: boolean
  handoverRequestHours: string | null
  handoverRequestTiming: HandoverTiming | null
  handoverRequestReference: HandoverReference | null
  handoverExpirationHours: string | null
  handoverExpirationTiming: HandoverTiming | null
  handoverExpirationReference: HandoverReference | null
  limitConsecutiveHours: boolean
  maxConsecutiveHours: string | null
  minRestHours: string | null
  // Ligado = a cadeia de horas consecutivas conta só plantões com our_team = true; default false.
  consecutiveHoursOurTeamOnly: boolean
}

export const LEAD_TIME_OPTIONS: HandoverSelectOption[] = [
  { title: '1 hora', value: '1' },
  { title: '2 horas', value: '2' },
  { title: '4 horas', value: '4' },
  { title: '6 horas', value: '6' },
  { title: '12 horas', value: '12' },
  { title: '24 horas', value: '24' },
  { title: '48 horas', value: '48' }
]

/**
 * Opções do prazo para SOLICITAR troca. Separada de LEAD_TIME_OPTIONS porque a
 * mesma lista também alimenta a expiração do convite, e ali `0` não faz sentido:
 * um convite que expira no instante em que nasce não pode ser aceito por ninguém.
 *
 * `0` = sem antecedência, a troca fica liberada até o próprio horário de
 * referência. O backend distingue isso de "não configurado" pelo `null`.
 */
export const REQUEST_LEAD_TIME_OPTIONS: HandoverSelectOption[] = [
  { title: '0 horas', value: '0' },
  ...LEAD_TIME_OPTIONS
]

// Opções da seção Jornada (máximo de horas consecutivas / pausa mínima para retornar).
// Independente de LEAD_TIME_OPTIONS: 1h/2h não fazem sentido para jornada e o teto do
// backend é 168h. Cobre os defaults (24/6), o valor 8h em uso real em dev e o 18h
// solicitado pelas unidades de Brasília.
export const CONSECUTIVE_HOURS_OPTIONS: HandoverSelectOption[] = [
  { title: '6 horas', value: '6' },
  { title: '8 horas', value: '8' },
  { title: '12 horas', value: '12' },
  { title: '16 horas', value: '16' },
  { title: '18 horas', value: '18' },
  { title: '24 horas', value: '24' },
  { title: '36 horas', value: '36' },
  { title: '48 horas', value: '48' },
  { title: '72 horas', value: '72' }
]

export const WHEN_OPTIONS: HandoverSelectOption<HandoverTiming>[] = [
  { title: 'Antes', value: 'BEFORE' },
  { title: 'Depois', value: 'AFTER' }
]

export const EVENT_OPTIONS: HandoverSelectOption<HandoverReference>[] = [
  { title: 'Início do plantão', value: 'STATION_START' },
  { title: 'Fim do plantão', value: 'STATION_END' }
]

// Campos exigidos quando o time tem trocas habilitadas + mensagem de erro (pt-BR).
export const TEAM_HANDOVER_VALIDATION_RULES: Record<string, string> = {
  handoverRequestHours: 'Antecedência do prazo de troca é obrigatória.',
  handoverRequestTiming: 'Selecione quando o prazo de troca se aplica.',
  handoverRequestReference: 'Selecione o evento de referência do prazo de troca.',
  handoverExpirationHours: 'Antecedência da expiração é obrigatória.',
  handoverExpirationTiming: 'Selecione quando a expiração se aplica.',
  handoverExpirationReference: 'Selecione o evento de referência da expiração.'
}

// Defaults aplicados no momento em que o gestor liga o toggle de jornada.
export const DEFAULT_MAX_CONSECUTIVE_HOURS = '24'
export const DEFAULT_MIN_REST_HOURS = '6'

// Campos exigidos quando o time tem o limite de jornada ligado + mensagem de erro (pt-BR).
export const TEAM_CONSECUTIVE_HOURS_VALIDATION_RULES: Record<string, string> = {
  maxConsecutiveHours: 'Máximo de horas consecutivas é obrigatório.',
  minRestHours: 'Pausa mínima para retornar é obrigatória.'
}

export function createDefaultTeamHandoverConfig(
  overrides: Partial<TeamHandoverConfig> = {}
): TeamHandoverConfig {
  return {
    allowDoctorStationHandover: false,
    requiresAdminApproval: true,
    handoverRequestHours: null,
    handoverRequestTiming: null,
    handoverRequestReference: null,
    handoverExpirationHours: null,
    handoverExpirationTiming: null,
    handoverExpirationReference: null,
    limitConsecutiveHours: false,
    maxConsecutiveHours: null,
    minRestHours: null,
    consecutiveHoursOurTeamOnly: false,
    ...overrides
  }
}

// Muta o config no lugar, espelhando o handleSelectRestriction original.
export function applyHandoverRestrictionMode(
  config: TeamHandoverConfig,
  mode: HandoverRestrictionMode
): void {
  if (mode === 'disabled') {
    config.allowDoctorStationHandover = false
    config.requiresAdminApproval = true
    config.handoverRequestHours = null
    config.handoverRequestTiming = null
    config.handoverRequestReference = null
    config.handoverExpirationHours = null
    config.handoverExpirationTiming = null
    config.handoverExpirationReference = null
    return
  }
  config.allowDoctorStationHandover = true
  config.requiresAdminApproval = mode === 'admin_only'
}

// Muta o config no lugar. Ao ligar, aplica os defaults sem sobrescrever escolha anterior;
// ao desligar, zera os dois valores (coerência exigida pelo backend).
export function applyConsecutiveHoursToggle(config: TeamHandoverConfig, enabled: boolean): void {
  config.limitConsecutiveHours = enabled
  if (!enabled) {
    config.maxConsecutiveHours = null
    config.minRestHours = null
    config.consecutiveHoursOurTeamOnly = false
    return
  }
  config.maxConsecutiveHours = config.maxConsecutiveHours ?? DEFAULT_MAX_CONSECUTIVE_HOURS
  config.minRestHours = config.minRestHours ?? DEFAULT_MIN_REST_HOURS
}

export function isTeamHandoverConfigValid(config: TeamHandoverConfig): boolean {
  // Jornada é validada sempre — não depende de allowDoctorStationHandover.
  if (config.limitConsecutiveHours && (!config.maxConsecutiveHours || !config.minRestHours)) {
    return false
  }
  if (!config.allowDoctorStationHandover) return true
  return Object.keys(TEAM_HANDOVER_VALIDATION_RULES).every(
    (field) => !!config[field as keyof TeamHandoverConfig]
  )
}

export function buildHandoverExampleText(
  leadTime: string | null,
  when: string | null,
  event: string | null
): string {
  // `leadTime` é string: '0' é truthy e passa aqui de propósito. Só '' / null
  // significam campo não preenchido.
  if (!leadTime || !when || !event) return ''
  const lead = REQUEST_LEAD_TIME_OPTIONS.find((o) => o.value === leadTime)
  const w = WHEN_OPTIONS.find((o) => o.value === when)
  const ev = EVENT_OPTIONS.find((o) => o.value === event)
  if (!lead || !w || !ev) return ''
  // Sem antecedência o "antes/depois" não diz nada — zero hora antes e zero
  // hora depois são o mesmo instante. O exemplo vira o evento puro.
  if (leadTime === '0') return ` no ${ev.title.toLowerCase()}`
  return ` ${lead.title} ${w.title.toLowerCase()} do ${ev.title.toLowerCase()}`
}

export function buildConsecutiveHoursExampleText(
  limitEnabled: boolean,
  maxHours: string | null,
  minRest: string | null,
  ourTeamOnly = false
): string {
  if (!limitEnabled) {
    return 'Sem restrição — médicos podem ser alocados sem limite de horas consecutivas.'
  }
  if (!maxHours || !minRest) {
    return 'Selecione o máximo de horas consecutivas e a pausa mínima para retornar.'
  }
  // Escopo explícito: a regra é desta escala, mas a contagem atravessa a unidade.
  // Dizer só "nesta unidade" levou gestores a desligar o toggle na escala errada.
  const scope = ourTeamOnly
    ? 'Contam só os plantões dele na unidade como equipe interna, de qualquer escala. '
    : 'Contam todos os plantões dele na unidade, de qualquer escala. '
  return (
    `Um médico não pode acumular mais de ${maxHours}h consecutivas ao ser alocado nesta escala. ` +
    scope +
    `Após atingir o limite, só poderá ser escalado novamente depois de ${minRest}h de pausa.`
  )
}

export interface ScheduleHandoverFields {
  allowDoctorStationHandover?: boolean | null
  requiresAdminApproval?: boolean | null
  handoverRequestHours?: number | null
  handoverRequestTiming?: string | null
  handoverRequestReference?: string | null
  handoverExpirationHours?: number | null
  handoverExpirationTiming?: string | null
  handoverExpirationReference?: string | null
  limitConsecutiveHours?: boolean | null
  maxConsecutiveHours?: number | null
  minRestHours?: number | null
  consecutiveHoursOurTeamOnly?: boolean | null
}

export function mapToTeamHandoverConfig(fields: ScheduleHandoverFields): TeamHandoverConfig {
  const toStr = (val?: number | null): string | null => (val != null ? String(val) : null)
  return {
    allowDoctorStationHandover: fields.allowDoctorStationHandover ?? false,
    requiresAdminApproval: fields.requiresAdminApproval ?? true,
    handoverRequestHours: toStr(fields.handoverRequestHours),
    handoverRequestTiming: (fields.handoverRequestTiming as HandoverTiming) ?? null,
    handoverRequestReference: (fields.handoverRequestReference as HandoverReference) ?? null,
    handoverExpirationHours: toStr(fields.handoverExpirationHours),
    handoverExpirationTiming: (fields.handoverExpirationTiming as HandoverTiming) ?? null,
    handoverExpirationReference: (fields.handoverExpirationReference as HandoverReference) ?? null,
    limitConsecutiveHours: fields.limitConsecutiveHours ?? false,
    maxConsecutiveHours: toStr(fields.maxConsecutiveHours),
    minRestHours: toStr(fields.minRestHours),
    consecutiveHoursOurTeamOnly: fields.consecutiveHoursOurTeamOnly ?? false
  }
}

const toHours = (value: string | null): number | null => (value ? Number(value) : null)

// Monta os 24 campos (12 + 12): 12 legados = Nossa Equipe + 12 *NotOurTeam = Equipe da Unidade
// prontos para UpdateSchedule / CreateFullScheduleRequest.schedule.
export function buildTeamHandoverPayload(
  ourTeam: TeamHandoverConfig,
  notOurTeam: TeamHandoverConfig
) {
  return {
    allowDoctorStationHandover: ourTeam.allowDoctorStationHandover,
    requiresAdminApproval: ourTeam.requiresAdminApproval,
    handoverRequestHours: toHours(ourTeam.handoverRequestHours),
    handoverRequestTiming: ourTeam.handoverRequestTiming ?? null,
    handoverRequestReference: ourTeam.handoverRequestReference ?? null,
    handoverExpirationHours: toHours(ourTeam.handoverExpirationHours),
    handoverExpirationTiming: ourTeam.handoverExpirationTiming ?? null,
    handoverExpirationReference: ourTeam.handoverExpirationReference ?? null,
    limitConsecutiveHours: ourTeam.limitConsecutiveHours,
    maxConsecutiveHours: toHours(ourTeam.maxConsecutiveHours),
    minRestHours: toHours(ourTeam.minRestHours),
    consecutiveHoursOurTeamOnly: ourTeam.consecutiveHoursOurTeamOnly,
    allowDoctorStationHandoverNotOurTeam: notOurTeam.allowDoctorStationHandover,
    requiresAdminApprovalNotOurTeam: notOurTeam.requiresAdminApproval,
    handoverRequestHoursNotOurTeam: toHours(notOurTeam.handoverRequestHours),
    handoverRequestTimingNotOurTeam: notOurTeam.handoverRequestTiming ?? null,
    handoverRequestReferenceNotOurTeam: notOurTeam.handoverRequestReference ?? null,
    handoverExpirationHoursNotOurTeam: toHours(notOurTeam.handoverExpirationHours),
    handoverExpirationTimingNotOurTeam: notOurTeam.handoverExpirationTiming ?? null,
    handoverExpirationReferenceNotOurTeam: notOurTeam.handoverExpirationReference ?? null,
    limitConsecutiveHoursNotOurTeam: notOurTeam.limitConsecutiveHours,
    maxConsecutiveHoursNotOurTeam: toHours(notOurTeam.maxConsecutiveHours),
    minRestHoursNotOurTeam: toHours(notOurTeam.minRestHours),
    consecutiveHoursOurTeamOnlyNotOurTeam: notOurTeam.consecutiveHoursOurTeamOnly
  }
}
